import { createFileRoute } from '@tanstack/react-router';
import { useIxcProxyvoteV1ProxyJobQueryServiceProxyJobGet } from '@interxfi/api-client/generated/hooks';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { PageTabRail } from '../../../../../../../../../../../../components/page-tab-rail/page-tab-rail';
import {
  type PortalTabbedNavigationTab,
  TabbedNavigationGroup,
} from '../../../../../../../../../../../../components/router/router-tabbed-navigation';
import { getProxyVoteJobDetailPath } from '../../../../../../../../../../../../features/proxyvote/job/proxyvote-job-routing';
import { ProxyVoteSecurityRecordsTable } from '../../../../../../../../../../../../features/proxyvote/proxyvote-security-records-table';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/beta-nxt/proxy-vote-job/_job/$jobId/_job/')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.ClearingVendorBetaNxtProxyVoteJob,
      label: 'Securities',
      order: 0,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.ClearingVendorBetaNxtProxyVoteJob>,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `proxyvote-job-${params.jobId}`,
        label: 'Job',
        to: getProxyVoteJobDetailPath(params.jobId),
        suffix: <CopyButton content={params.jobId} label="Copy Job ID" tooltip={{ copy: params.jobId, copied: 'Copied Job ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  component: ProxyVoteJobSecurities,
});

function TableHeader({ filter }: CustomTableHeaderProps) {
  return (
    <PageTabRail
      actions={
        filter ? (
          <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
            {filter}
          </HorizontalStack>
        ) : undefined
      }
      group={TabbedNavigationGroup.ClearingVendorBetaNxtProxyVoteJob}
    />
  );
}

function ProxyVoteJobSecurities() {
  const { jobId } = Route.useParams();
  const { data, isPending, error } = useIxcProxyvoteV1ProxyJobQueryServiceProxyJobGet({ jobId });
  const showPending = Boolean(isPending || error);

  return (
    <ProxyVoteSecurityRecordsTable
      data={data?.proxyJob?.data?.spec?.securities ?? STABLE_EMPTY_ARRAY_REF}
      HeaderActions={TableHeader}
      showSkeleton={showPending}
    />
  );
}
